import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  VueServerMessage, VueServerMessageType,
  VueSessionCookieService,
  VueTranslationService
} from '@vue/vueframework';
import { TestCenterAffiliate } from '../shared/types/test-center-affiliate.model';

@Component({
  selector: 'app-affiliates',
  templateUrl: './affiliates.component.html',
  standalone: false
})
export class AffiliatesComponent implements OnInit {
  /**
   * Map holding the affiliate list on each page
   */
  public affiliatePageMap = new Map<number, TestCenterAffiliate[]>();

  /**
   * Holds the list of affiliates to be displayed in the current page
   */
  public currentPageAffiliates: TestCenterAffiliate[];

  /**
   * Filtered list of affiliates
   */
  public filteredAffiliates: TestCenterAffiliate[] = [];

  /**
   * Error messages to the displayed on screen
   */
  public messages: VueServerMessage[] = [];

  /**
   * Logged in user site detail
   */
  public realmDetail;

  /**
   * Search input
   */
  public searchText = '';

  /**
   * Total list of affiliates
   */
  public siteAffiliates: TestCenterAffiliate[];

  /**
   * Holds the current sort field
   */
  public sortField = '';

  /**
   * Total number of rows
   */
  public totalRows = 0;

  /**
   * Page number
   */
  public pageNumber = 0;

  /**
   * Number of rows in the current page
   */
  public pageRows = 0;

  /**
   * Default page size
   */
  public pageSize = 50;

  public constructor(
    private route: ActivatedRoute,
    private vueSessionCookieService: VueSessionCookieService,
    private vueTranslationService: VueTranslationService,
    private router: Router
  ) { }

  public ngOnInit(): void {
    this.messages = [];
    this.realmDetail = JSON.parse(this.vueSessionCookieService.getAttribute('realmDetail', 'VUE'));
    this.siteAffiliates = this.route.snapshot.data.siteAffiliateList;

    if (this.siteAffiliates === null) {
      this.handleError();
      this.siteAffiliates = [];
    } if (this.siteAffiliates.length === 0) {
      this.totalRows = 0;
    } else {
      // Pin the local site to the top of the list
      const localSite = this.siteAffiliates.find((affiliate) => {
        return affiliate.siteId.toString() === this.realmDetail.siteID;
      });
      this.filteredAffiliates = this.siteAffiliates.filter((affiliate) => {
        return affiliate.name.indexOf('') !== -1 && affiliate.siteId.toString() !== this.realmDetail.siteID;
      });
      this.filteredAffiliates.splice(0, 0, localSite);
      this.totalRows = this.filteredAffiliates.length;
      this.pageSizeChange(this.pageSize);
    }
  }

  /**
   * Switch the context to the given affiliate site
   *
   * @param affiliate
   *    Site to which the context is switched
   */
  public onSwitchAffiliate(affiliate: TestCenterAffiliate): void {
    this.vueSessionCookieService.setAttribute('siteID', affiliate.siteId.toString(), 'VUE');
    this.router.navigateByUrl('/pleaseSelect');
  }

  /**
   * Method used to handle change in moving from one page to another
   *
   * @param pageNumber
   *    Current page number
   */
  public pageNumberChange(pageNumber: number): void {
    this.pageNumber = pageNumber;
    if (this.affiliatePageMap.get(pageNumber)) {
      this.currentPageAffiliates = this.affiliatePageMap.get(pageNumber);
    } else {
      this.currentPageAffiliates = [];
    }
  }

  /**
   * This method is used to calculate/recalculate the list of pages
   *
   * NOTE: When the backend services are rewritten in REST move pagination functionality to the service
   *
   * @param size
   *    Size of the page
   */
  public pageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageRows = size;
    if (size > this.totalRows) {
      this.pageRows = this.totalRows;
    }
    const pageCount = Math.ceil(this.totalRows / this.pageSize);
    let pageNumber = 1;
    let startIndex = 0;
    let endIndex: number = this.pageRows;
    while (pageNumber <= pageCount) {
      this.affiliatePageMap.set(pageNumber, this.filteredAffiliates.slice(startIndex, endIndex));
      pageNumber++;
      startIndex = endIndex;
      endIndex = startIndex + (size);
      if (endIndex > this.totalRows) {
        endIndex = this.totalRows;
      }
    }
    if (this.totalRows === 0) {
      this.affiliatePageMap.clear();
    }
    this.pageNumberChange(1);
  }

  /**
   * Filter the affiliate list based on the search text. Simple case insensitive string search.
   */
  public searchSites(): void {
    this.filteredAffiliates = this.siteAffiliates.filter((affiliate) => {
      return affiliate.siteId.toString().indexOf(this.searchText) !== -1 ||
        affiliate.name.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1 ||
        (affiliate.city && affiliate.city.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1) ||
        (affiliate.state && affiliate.state.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1) ||
        affiliate.country.toLowerCase().indexOf(this.searchText.toLowerCase()) !== -1;
    });
    this.totalRows = this.filteredAffiliates.length;
    this.pageSizeChange(this.pageSize);
  }

  /**
   * Method used to handle the sorting of rows.
   *
   * @param fieldName
   *    Field based on which the sorting is done
   */
  public sortChange(fieldName: string): void {
    this.sortField = fieldName;
    const list = this.filteredAffiliates;

    if (fieldName === 'siteID') {
      list.sort((left, right) => left.siteId - right.siteId);
    } else if (fieldName === '-siteID') {
      list.sort((left, right) => right.siteId - left.siteId);
    } else if (fieldName.startsWith('-')) {
      list.sort((left, right) => this.compare(right[ fieldName.slice(1) ], left[ fieldName.slice(1) ]));
    } else {
      list.sort((left, right) => this.compare(left[ fieldName ], right[ fieldName ]));
    }
    
    this.filteredAffiliates = [ ...list ];
    this.pageSizeChange(this.pageSize);
    this.pageNumberChange(1);
  }
  
  public compare(a: string, b: string): number {
    return (a || '').toLocaleLowerCase().localeCompare((b || '').toLocaleLowerCase());
  }

  /**
   * This method is used to handle any server side errors during the service calls
   */
  private handleError(): void {
    this.messages = [
      ...this.messages,
      new VueServerMessage(
        VueServerMessageType.Error,
        this.vueTranslationService.getTranslation('PortalBase', 'error.UnknownError'),
        false)
    ];
  }
}
